import { useHardwareInteractions } from './hardwareInteractionsRun';
import { useRun } from './categoryRun';
import { usePartsRun } from './partsRun';
import { useStepsRun, subStepLabel } from './stepsRun';
import { useStepParts } from './stepPartsRun';
import { useAssemblySteps } from './categoryBands';
import { useImagesBusy } from './imagesRefresh';
import { HOLES_PHASE, STEPS_PHASE, useWorkingElsewhere } from './phaseWorking';
import { useRunsInFlight } from './generatorMemory';
import { useJointsRunning } from './JointsRun';
import { useFixingCollisions } from './ProductOverlaps';
import {
  useAllocStage, useAnimationsStage, usePartImagesStage, usePartModelsStage,
  usePartsListStage, useSubstepsStage,
} from './stageProgress';
import { usePublishStatus } from './publishStatus';
import { useResolvedInfo } from './infoPicks';
import { hasInfo, useBrand } from './brandStore';
import { useListing } from './listingStore';
import { useAssemblySlots } from './assemblySlots';
import { useClips } from './assemblyClip';
import { appliedIdOf, useMaterialsRun } from './materialsRun';
import { useModelsStages } from './modelsStages';
import { useSubmodels } from './listingStore';
import { useBusyKeys } from './phaseBusy';

// One place that answers "how far along is this document?", phase by phase.
//
// Phase 1 is the manual itself (uploaded and rendered); the rest are the pipeline. No
// pipeline phase ever recorded a "done" flag — each one only knows whether it is busy — so
// completion is derived from the results each store holds. Everything here is read through
// the module-scope, document-keyed stores, which is why the bar keeps its state when the
// user switches phase tabs (the panels unmount, the stores don't).
//
// Phase 2 is the parts in all the ways they are known — listed, photographed,
// reconstructed — measured as one phase with several stages. The two questions about the
// steps are a phase each: what a step is made of, and how the parts are shared out among
// them.

export type PhaseState = {
  n: number;
  // 0..1. A phase reports partial progress while it streams results in, so the bar
  // advances inside a phase rather than jumping in sixths.
  fraction: number;
  busy: boolean;
  done: boolean;
};

export type Progress = {
  // In phase order. Each one carries its own done/busy/fraction, which is all the bar
  // needs: the count of finished phases is the count of full segments, and the one
  // after them is the phase left to do.
  phases: PhaseState[];
};

// A phase's measure, before it has a number: the number is its position in the list below.
type Measure = Omit<PhaseState, 'n'>;

const clamp01 = (n: number) => (Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 0);

// `docLoaded`: the PDF is open and its pages counted — shown in Phase 1's Manual subphase.
// Every phase is numbered by its position in the list returned below.
export function usePhaseProgress(docId: string, docLoaded: boolean): Progress {
  const run = useRun(docId);
  const parts = usePartsRun(docId);
  const alloc = useStepParts(docId);
  const st = useStepsRun(docId);
  const { steps } = useAssemblySteps(docId);
  const imagesBusy = useImagesBusy(docId);
  const badgesBusy = useBusyKeys();
  // A RUN IN ANOTHER TAB, or another admin's. Every phase's busy flag below is this
  // browser's own memory — a run lives in the tab that started it — so without this a
  // second reader of a shared manual draws a phase nobody is running while it is being
  // re-run in front of them. See phaseWorking, which is the only thing here that asks the
  // server a question of its own.
  const elsewhere = useWorkingElsewhere(docId);
  // Phase 5's runs, each kept by manual outside the panel that started it.
  const flights = useRunsInFlight(docId);
  // The joints' run, by the phase its joints are: the hardware ones are this phase's, the covers
  // ones the assembly's.
  const drilling = useJointsRunning(docId, 'holes');
  const placing = useJointsRunning(docId, 'covers');
  const fixingCollisions = useFixingCollisions(docId);
  // The stages phase 2 and phase 3 are made of, from the one place that measures them —
  // the same numbers the switcher inside each phase draws on its cards.
  const listStage = usePartsListStage(docId);
  const imagesStage = usePartImagesStage(docId);
  const modelsStage = usePartModelsStage(docId);
  // The models phase's subphases together — see useModelsStages.
  const modelsSubs = useModelsStages(docId);
  // Every colourway's applied materials reading — see the materials phase below.
  const materials = useMaterialsRun(docId);
  const colourways = useSubmodels(docId);
  const appliedCount = colourways.filter((c) => !!appliedIdOf(materials, c.id)).length;
  const allocStage = useAllocStage(docId);
  const substepsStage = useSubstepsStage(docId);
  const hardwareTable = useHardwareInteractions(docId);
  const publish = usePublishStatus(docId);
  const animationsStage = useAnimationsStage(docId);
  const assemblySlots = useAssemblySlots(docId);
  // THE CLIPS THAT WOULD ACTUALLY BE PUBLISHED — see useClips, and Phase 6 below,
  // which measures itself against them rather than against a second reading of the
  // same question.
  const clips = useClips(docId);
  const info = useResolvedInfo(docId);
  // Read for their `detecting` flags: the two calls that run beside the
  // classification and outlast it.
  const brandInfo = useBrand(docId);
  const listing = useListing(docId);

  // Phase 1 — page classification, PLUS the general info read off the manual and
  // (optionally) off the web listing. One click starts all of them and the phase is
  // over only when all of them are over, so all of them have to be counted here.
  // Reading the classification alone is why this bar used to fill while the info
  // table was still spinning: the classification is many small calls and finishes
  // first, the info is one long call over every page and finishes last.
  //
  // The phase is also not done while the two sources disagree, however complete the
  // work is: the answer is still owed to the user, and every later phase reads what
  // they decide.
  const infoBusy = brandInfo.detecting || listing.detecting;
  const classified = Object.keys(run.segments).length > 0;
  const classifyDone = classified && !infoBusy && !info.blocked;

  // Phase 1 is two or three reads, and the bar counts them all.
  //
  // It used to measure the classification alone — pages done over pages total — while
  // the other reads only capped it at 0.9. So it said "80% of the pages are sorted"
  // while looking like "80% of this phase is done", and those are different quantities:
  // the classification is many small calls over the pages, the info read is one long
  // call across the whole manual, the listing is a fetch plus a filter call. Two of the
  // three could not move it at all.
  //
  // One unit each, which is coarse and honest, rather than precise about a third of the
  // work and silent about the rest. The units are wildly unequal in duration and there
  // is no way to know that in advance — a manual has 4 pages or 40 — so the bar jumps
  // rather than glides. It says what it means.
  //
  // A run that has started reads zero, not what the last one reached: `progress` is null
  // between the launch and the first block reporting, and the segments on screen then belong
  // to the run being replaced.
  const pagesFraction = run.progress
    ? clamp01(run.progress.done / run.progress.total)
    : run.busy
      ? 0
      : classified
        ? 1
        : 0;
  // A read counts as landed by the same test that decides its reply was worth keeping.
  // The listing's error counts too: a listing that 404s is read — the answer is that
  // there isn't one — and a unit that can never fill would hold the bar down forever.
  const infoRead = !brandInfo.detecting && hasInfo(brandInfo);
  const listingRead = !listing.detecting && (!!listing.raw.trim() || !!listing.error);
  // No listing URL, no listing read: the feature is optional, and a phase cannot be
  // two-thirds done because of work nobody asked for.
  //
  // AND THE UPLOAD, a unit like the reads: the manual opened and paginated is what every
  // read waits on.
  const units = [docLoaded ? 1 : 0, pagesFraction, infoRead ? 1 : 0, ...(listing.url.trim() ? [listingRead ? 1 : 0] : [])];
  const classifyFraction = units.reduce((a, b) => a + b, 0) / units.length;

  const classify: Measure = {
    busy: !docLoaded || !!run.busy || infoBusy,
    done: docLoaded && classifyDone,
    // Held short of full until the phase is actually over — which includes the user
    // settling any disagreement between the two sources. A bar that reads 100% next to
    // a running spinner, or next to a question, is worse than one that reads 90%: it
    // says the phase is over when it demonstrably isn't.
    fraction: docLoaded && classifyDone ? 1 : Math.min(classifyFraction, 0.9),
  };

  // Phase 2 — the parts, in the two ways the MANUAL knows them: listed off it, and
  // photographed. The third way — reconstructed — is a phase of its own now (see
  // viewerPipelines), and its stage is counted there.
  //
  // EQUAL WEIGHT to each stage, and each stage is already counted per part, so the bar reads
  // as "how much of what is owed about these parts has been done" rather than favouring the
  // cheap stage over the paid one.
  const partStages = [listStage, imagesStage];
  const partsDone = partStages.every((st) => st.done);
  const partsPhase: Measure = {
    busy: parts.busy || imagesBusy,
    done: partsDone,
    fraction: partStages.reduce((a, st) => a + st.fraction, 0) / partStages.length,
  };

  // Phase 4 — the steps, in two halves. The OVERVIEW first: which parts and tools each step
  // adds, one call over the manual, so it has landed or it has not.
  //
  // A manual with no steps is owed nothing — but only once the classification has run and
  // found none. Before that, "no steps" is nobody having looked, which is not an answer.
  const noStepsToShare = classifyDone && steps.length === 0;
  const allocDone = noStepsToShare || allocStage.done;
  const allocPhase: Measure = {
    busy: alloc.busy,
    done: allocDone,
    fraction: allocDone ? 1 : 0,
  };

  const stepsDone = substepsStage.done;

  // …and its SUB-STEPS: what each step is made of, one call per step, so this is per step. It
  // reads the overview above, which is why it follows it.
  //
  // AND THE ONE PHASE THAT CAN BE BUSY SOMEWHERE ELSE. Its run is the long one — a call per
  // step, minutes of them — and the manual it runs on is shared, so it is the one a second
  // reader actually meets in flight. `st.busy` is this tab; `here` is any tab.
  //
  // The fraction follows whoever is running. This tab's own is derived from the answers it
  // holds, and a full re-run empties those to draw "waiting" on every step — so a reader
  // watching from outside would see the bar fall to zero and stay there. What the running
  // tab reports is how far it has got, which is what the bar is for.
  const stepsElsewhere = elsewhere?.phase === STEPS_PHASE ? elsewhere : null;
  const stepsPhase: Measure = {
    busy: st.busy || !!stepsElsewhere,
    done: stepsDone,
    fraction: !st.busy && stepsElsewhere && stepsElsewhere.total > 0
      ? clamp01(stepsElsewhere.done / stepsElsewhere.total)
      : substepsStage.fraction,
  };

  // THE PHASE IS BOTH HALVES: busy while either is out, done when both are, and half the bar
  // each — the overview is one call, but it is the half every step's analysis is given.
  const stepsWhole: Measure = {
    busy: allocPhase.busy || stepsPhase.busy,
    done: allocDone && stepsDone,
    fraction: (allocPhase.fraction + stepsPhase.fraction) / 2,
  };

  // Phase 3 — the materials: one applied reading per colourway, which every mesh is built
  // with. Done when each has one; busy while a read, a crop or an apply is out.
  const materialsPhase: Measure = {
    busy: materials.reading.size > 0 || materials.cropping.size > 0
      || materials.applying.size > 0,
    done: colourways.length > 0 && appliedCount === colourways.length,
    fraction: colourways.length ? clamp01(appliedCount / colourways.length) : 0,
  };

  // Phase 5 — the meshes: the whole product kept and cut into every component, the hardware
  // interactions read, a model for every piece of hardware, and every component's holes drilled
  // (see useModelsStages).
  //
  // Its own phase, and its own share of the bar. It was a third of Phase 2's, which made a
  // manual whose parts were listed and photographed read as two thirds done when the paid,
  // per-part half of the work had not started.
  const modelsPhase: Measure = {
    // WHILE ANY OF ITS SUBPHASES RUNS: a model generated (the whole product, a hardware set, a
    // part), Fix collisions, the hardware table, Holes & covers — or Find holes in another tab.
    busy: hardwareTable.busy || Object.keys(flights).length > 0 || fixingCollisions
      || drilling || elsewhere?.phase === HOLES_PHASE,
    done: modelsSubs.all.done,
    fraction: modelsSubs.all.fraction,
  };

  // Phase 6 — the assembly: one signed-off animation per sub-step of the manual.
  //
  // COUNTED AGAINST THE SUB-STEPS, and against VALIDATED ones. It used to fill on the first
  // fitting anybody wrote, which made a bar that said "done" over one sub-step out of twelve
  // — and it counted a fitting as soon as it named two parts, which is a thing you can do in
  // two clicks and be wrong about. A fitting with the right parts and a wrong axis is
  // complete and wrong; only somebody watching it can tell, so only somebody watching it
  // ticks it.
  //
  // A SUB-STEP THAT FITS NOTHING IS NO LONGER IN HERE. "Turn the completed chair upright"
  // used to be a gesture of its own, sitting in this denominator waiting for an animation
  // that had no reason to exist — the bar stalled one short of full on any manual with one.
  // Positioning a single part is not a sub-step any more (see subStepKinds), and a step whose
  // whole content was that contributes no slot at all (see assemblySlots), so what is counted
  // here is gestures somebody can actually watch.
  //
  // BUSY WHILE ITS COVERS ARE PLACED (Placing covers): the rest costs nothing and calls nothing.
  //
  // The count itself is useAnimationsStage's, which the card over the animations draws too.
  const assemblyPhase: Measure = {
    busy: placing,
    done: animationsStage.done,
    fraction: animationsStage.fraction,
  };

  // Phase 7 — publication. What it publishes is the manual as read: the pages classified,
  // the parts listed and photographed and reconstructed, the steps broken into sub-steps.
  // So it is done when the page was published and all of that is in it.
  //
  // The assembly is deliberately NOT in that list. Publication draws the phases it prints,
  // and it does not print the assembly yet — gating on a phase whose result never reaches
  // the page would block publishing on work the reader cannot see. It joins this line the
  // day the public page draws a step's movement, together with the staleness check that
  // belongs beside it: published_at against the newest fitting.
  const publishDone =
    publish.published && stepsDone && allocDone && classifyDone && partsDone
    && modelsStage.done;

  // HOW MUCH OF THE MANUAL WOULD ACTUALLY REACH A VISITOR: the sub-steps with a video, over
  // the sub-steps there are.
  //
  // It was published-or-not, 1 or 0, which is a fact about a button having been pressed
  // rather than about the thing published. A page can go out with three screens of eight
  // showing a gesture and five showing nothing, and the bar read the same as one where every
  // screen has its clip — so the one number that says whether the tutorial is worth visiting
  // was the one number this phase could not report.
  //
  // OFF useClips AND NOT A SECOND READING OF "validated". The publisher drops a sub-step
  // whose fitting is unvalidated (see PublishStep), and so does this — but useClips drops two
  // more kinds that a validated flag alone would count: a fitting whose parts were cleared
  // after the tick, and one whose parts have no mesh to draw. Both publish an empty screen,
  // and a bar promising a clip that the page cannot show is worse than no bar. Same function,
  // so the promise and the page cannot disagree.
  //
  // SUB-STEPS ONLY IN THE DENOMINATOR, and counted over the SLOTS rather than over the clip
  // keys — the same care Phase 6 above takes, and for the same reason. A slot with no letter
  // is a whole step nobody has broken down: not a screen, so not a screen that is missing
  // its video.
  const screens = assemblySlots.filter((s) => s.step != null && s.letter != null);
  const filmed = screens.filter(
    (s) => clips[subStepLabel(s.step, s.letter)],
  ).length;
  const publication: Measure = {
    busy: false,
    done: publishDone,
    fraction: screens.length ? clamp01(filmed / screens.length) : 0,
  };

  // The cards are paired with these BY POSITION (see PhaseNav), and numbered by it — so this is
  // the pipeline's order (viewerPipelines) and nothing else: one out of place, and every card
  // after it draws its neighbour's bar.
  const measures = [
    classify, partsPhase, materialsPhase, stepsWhole, modelsPhase,
    assemblyPhase, publication,
  ];
  // AND ANY BADGE SPINNING IN IT, whatever store its call lands in (see phaseBusy).
  return {
    phases: measures.map((m, i) => ({ ...m, n: i + 1, busy: m.busy || badgesBusy.has(String(i + 1)) })),
  };
}
